export class PermissionDeniedError extends Error {
  constructor() {
    super("permission_denied");
    this.name = "PermissionDeniedError";
  }
}

export class RegistrationCancelledError extends Error {
  constructor() {
    super("registration_cancelled");
    this.name = "RegistrationCancelledError";
  }
}

export interface RegisterDeviceTokenDeps<TPermissionStatus, TPlatform extends string> {
  requestPermission: () => Promise<TPermissionStatus>;
  isPermissionGranted: (status: TPermissionStatus) => boolean;
  getToken: () => Promise<string>;
  getDevicePlatform: () => TPlatform;
  registerToken: (token: string, platform: TPlatform) => Promise<void>;
  unregisterToken: (token: string) => Promise<void>;
  shouldRemainRegistered?: ((token: string) => boolean) | undefined;
  onRollbackError?: (error: unknown) => void;
}

export async function registerDeviceTokenWithGuard<TPermissionStatus, TPlatform extends string>({
  requestPermission,
  isPermissionGranted,
  getToken,
  getDevicePlatform,
  registerToken,
  unregisterToken,
  shouldRemainRegistered,
  onRollbackError,
}: RegisterDeviceTokenDeps<TPermissionStatus, TPlatform>): Promise<{ token: string }> {
  const status = await requestPermission();
  if (!isPermissionGranted(status)) {
    throw new PermissionDeniedError();
  }

  const token = await getToken();
  if (shouldRemainRegistered && !shouldRemainRegistered(token)) {
    throw new RegistrationCancelledError();
  }

  await registerToken(token, getDevicePlatform());

  if (shouldRemainRegistered && !shouldRemainRegistered(token)) {
    try {
      await unregisterToken(token);
    } catch (error: unknown) {
      onRollbackError?.(error);
    }
    throw new RegistrationCancelledError();
  }

  return { token };
}
